import { createContext, useContext } from 'react';

import type { ElectricalProjectInspectionKind, ElectricalProjectWithNorms } from '../validators/electrical-project';

/**
 *  A hook that returns the current project state.
 *
 *  There are three possible states for **`inProject`**:
 *  - `null` - The project state is still loading.
 *    - **`currentProject`** - `null`
 *  - `false` - There is no project.
 *    - **`currentProject`** - `null`
 *  - `true` - The project exists and user has permission.
 *    - **`currentProject`** - `Project`
 */

export const ElectricalProjectContext = createContext<ElectricalProjectWithNorms | null>(
    null,
);

export function useElectricalProject() {
    const context = useContext(ElectricalProjectContext);

    if (!context)
        throw new Error('Can\'t use `useProject` outside of the `ElectricalProjectProvider`!');

    if (!context.electricalProjectVersionId)
        throw new Error('Project not found');

    /**
     * @deprecated Use `api.useMutation('put', '/api/v2/electrical/projects/norms/patch', {
     *     onSuccess: () => {
     *         queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: projectVersionId }));
     *     },
     * });` instead.
     */
    function setSelectedNorms(_selectedNorms?: string[] | null) {
        throw new Error('Not implemented: Deprecated');
    }

    // Helper: Convert Set to array for mapping/iteration
    const inspectionKindsArray = Array.from(context.selectedInspectionKinds);

    // Helper: Check if a specific inspection kind is selected
    const hasInspectionKind = (kind: ElectricalProjectInspectionKind) =>
        context.selectedInspectionKinds.has(kind);

    return {
        context,
        setSelectedNorms,
        inspectionKindsArray,
        hasInspectionKind,
    };
}
